import { useEffect, useRef } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://pearlfoods.pk';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalPath?: string;
  image?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  type?: string;
  locale?: string;
}

export function useSEO({
  title,
  description,
  keywords,
  canonicalPath,
  image,
  noindex = false,
  jsonLd,
  type = 'website',
  locale = 'en_US',
}: SEOProps) {
  const scriptRefs = useRef<HTMLScriptElement[]>([]);

  useEffect(() => {
    if (title) {
      document.title = title;
    }

    const metaTags: { name?: string; property?: string; content: string }[] = [];
    if (description) {
      metaTags.push({ name: 'description', content: description });
    }
    if (keywords) {
      metaTags.push({ name: 'keywords', content: keywords });
    }
    if (noindex) {
      metaTags.push({ name: 'robots', content: 'noindex, nofollow' });
    } else {
      metaTags.push({ name: 'robots', content: 'index, follow' });
    }
    if (description) {
      metaTags.push({ property: 'og:description', content: description });
    }
    if (title) {
      metaTags.push({ property: 'og:title', content: title });
    }
    metaTags.push({ property: 'og:type', content: type });
    metaTags.push({ property: 'og:locale', content: locale });
    if (image) {
      metaTags.push({ property: 'og:image', content: image });
    }
    if (title) {
      metaTags.push({ name: 'twitter:title', content: title });
    }
    if (description) {
      metaTags.push({ name: 'twitter:description', content: description });
    }
    if (image) {
      metaTags.push({ name: 'twitter:image', content: image });
    }

    const createdElements: HTMLElement[] = [];

    metaTags.forEach((tag) => {
      const existing = document.querySelector(
        tag.property
          ? `meta[property="${tag.property}"]`
          : `meta[name="${tag.name}"]`
      );
      if (existing) {
        existing.setAttribute('content', tag.content);
      } else {
        const meta = document.createElement('meta');
        if (tag.property) {
          meta.setAttribute('property', tag.property);
        } else if (tag.name) {
          meta.setAttribute('name', tag.name);
        }
        meta.setAttribute('content', tag.content);
        document.head.appendChild(meta);
        createdElements.push(meta);
      }
    });

    // Canonical
    const canonicalUrl = canonicalPath ? `${SITE_URL}${canonicalPath}` : SITE_URL;
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonicalLink) {
      canonicalLink.href = canonicalUrl;
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      canonicalLink.href = canonicalUrl;
      document.head.appendChild(canonicalLink);
      createdElements.push(canonicalLink);
    }

    // JSON-LD
    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      schemas.forEach((schema, index) => {
        const id = `jsonld-${index}`;
        let existing = document.getElementById(id) as HTMLScriptElement | null;
        const content = JSON.stringify(schema, null, 2);
        if (existing) {
          existing.textContent = content;
        } else {
          existing = document.createElement('script');
          existing.type = 'application/ld+json';
          existing.id = id;
          existing.textContent = content;
          document.head.appendChild(existing);
          scriptRefs.current.push(existing);
        }
      });
    }

    return () => {
      createdElements.forEach((el) => el.remove());
      scriptRefs.current.forEach((el) => el.remove());
      scriptRefs.current = [];
    };
  }, [title, description, keywords, canonicalPath, image, noindex, jsonLd, type, locale]);
}

export default function SEO(props: SEOProps) {
  useSEO(props);
  return null;
}